<script setup>
import { ref, computed, watch } from "vue";
import { Inertia as router } from "@inertiajs/inertia";
import { Link } from "@inertiajs/inertia-vue3";
import SelectSearch from "@/Components/SelectSearch.vue";
import CardBoxModal from "@/Components/CardBoxModal.vue";
import Preview from "@/Components/Preview.vue";
import NotificationAlert from "@/Components/NotificationAlert.vue";
import axios from "axios";
import {
    mdiEye,
    mdiPencil,
    mdiTrashCan,
    mdiDownload,
    mdiMagnify,
    mdiBroom,
    mdiCheckCircle,
    mdiCloseCircle,
    mdiFileDocumentOutline,
    mdiLayersTriple,
    mdiCalendarRange,
    mdiChevronLeft,
    mdiChevronRight
} from "@mdi/js";
import BaseIcon from "@/Components/BaseIcon.vue";

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    },
    columnas: {
        type: Array,
        required: true,
        default: () => []
    },
    originType: {
        type: String,
        default: ""
    },
    objectTarget: {
        type: Array,
        default: () => []
    },
    filesDateDiffDays: {
        type: Number,
        default: 15
    },
    ruta: {
        type: String,
        default: "file"
    },
    modulo: {
        type: String,
        default: "Documento"
    },
    canEdit: {
        type: Boolean,
        default: true
    },
    canView: {
        type: Boolean,
        default: true
    },
    canDownload: {
        type: Boolean,
        default: true
    },
    deleteable: {
        type: Boolean,
        default: true
    }
});

// Normalización de columnas para compatibilidad
const cols = computed(() => {
    return props.columnas.map(c => {
        const key = c.col || c.key || "";
        const label = c.name || c.label || key;
        const type = c.type || "text";
        const replace = c.replace || {};
        return {
            key,
            label,
            type,
            replace,
            filterType: type === "datetime" || type === "date" ? "date" : (Object.keys(replace).length > 0 ? "select" : "text")
        };
    });
});

// Filtros por columna y búsqueda rápida
const filters = ref({});
const searchGeneral = ref("");
const sortKey = ref("");
const sortAsc = ref(true);

cols.value.forEach(c => {
    filters.value[c.key] = "";
});

const clearFilters = () => {
    Object.keys(filters.value).forEach(k => (filters.value[k] = ""));
    searchGeneral.value = "";
};

// Opciones para SelectSearch en filtros de columna
const getSelectFilterOptions = (col) => {
    if (col.replace && Object.keys(col.replace).length > 0) {
        return Object.entries(col.replace).map(([val, label]) => ({
            id: val,
            label: String(label)
        }));
    }
    const set = new Set();
    props.data.forEach(row => {
        const v = row[col.key];
        if (v !== undefined && v !== null && v !== "") {
            set.add(v);
        }
    });
    return Array.from(set).map(v => ({ id: v, label: String(v) }));
};

// Datos filtrados y ordenados
const filteredData = computed(() => {
    let result = [...props.data];

    // Búsqueda general
    if (searchGeneral.value.trim()) {
        const q = searchGeneral.value.toLowerCase().trim();
        result = result.filter(row => {
            return cols.value.some(col => {
                let cellVal = row[col.key];
                if (col.replace && col.replace[cellVal]) cellVal = col.replace[cellVal];
                return String(cellVal || "").toLowerCase().includes(q);
            });
        });
    }

    // Filtros específicos por columna
    Object.keys(filters.value).forEach(key => {
        const filterVal = filters.value[key];
        if (filterVal !== "" && filterVal !== null && filterVal !== undefined) {
            const colDef = cols.value.find(c => c.key === key);
            result = result.filter(row => {
                let val = row[key];
                if (colDef && colDef.filterType === "select") {
                    return String(val) === String(filterVal);
                }
                if (colDef && colDef.replace && colDef.replace[val]) {
                    val = colDef.replace[val];
                }
                return String(val || "").toLowerCase().includes(String(filterVal).toLowerCase());
            });
        }
    });

    // Ordenamiento
    if (sortKey.value) {
        result.sort((a, b) => {
            let va = a[sortKey.value];
            let vb = b[sortKey.value];
            if (va < vb) return sortAsc.value ? -1 : 1;
            if (va > vb) return sortAsc.value ? 1 : -1;
            return 0;
        });
    }

    return result;
});

const setSort = (key) => {
    if (sortKey.value === key) {
        sortAsc.value = !sortAsc.value;
    } else {
        sortKey.value = key;
        sortAsc.value = true;
    }
};

// Paginación
const perPage = ref(25);
const currentPage = ref(1);
const totalPages = computed(() => Math.max(1, Math.ceil(filteredData.value.length / perPage.value)));

watch([filteredData, perPage], () => {
    currentPage.value = 1;
});

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * perPage.value;
    return filteredData.value.slice(start, start + perPage.value);
});

// Selección Múltiple y Acciones Masivas
const checkedRows = ref([]);

const isAllChecked = computed(() => {
    return (
        paginatedData.value.length > 0 &&
        paginatedData.value.every(row => checkedRows.value.includes(row.id))
    );
});

const toggleSelectAll = (e) => {
    if (e.target.checked) {
        paginatedData.value.forEach(row => {
            if (!checkedRows.value.includes(row.id)) checkedRows.value.push(row.id);
        });
    } else {
        const paginatedIds = paginatedData.value.map(r => r.id);
        checkedRows.value = checkedRows.value.filter(id => !paginatedIds.includes(id));
    }
};

const toggleRowCheck = (id) => {
    const idx = checkedRows.value.indexOf(id);
    if (idx > -1) {
        checkedRows.value.splice(idx, 1);
    } else {
        checkedRows.value.push(id);
    }
};

// Modal de Acciones Masivas para reasignar src_object_id
const isMassModalActive = ref(false);
const massTargetId = ref(0);
const isProcessingMass = ref(false);

const openMassChangeModal = () => {
    massTargetId.value = 0;
    isMassModalActive.value = true;
};

// Helpers de fecha para acción masiva de fechas
const formatDateToISO = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const getTodayDate = () => formatDateToISO(new Date());

const getDefaultEndDate = (diffDays = 15) => {
    const d = new Date();
    d.setDate(d.getDate() + parseInt(diffDays || 15));
    return formatDateToISO(d);
};

// Modal de Acciones Masivas para cambiar fechas de inicio y fin
const isMassDateModalActive = ref(false);
const massStartDate = ref("");
const massEndDate = ref("");
const isProcessingMassDate = ref(false);

const openMassDateModal = () => {
    massStartDate.value = getTodayDate();
    massEndDate.value = getDefaultEndDate(props.filesDateDiffDays);
    isMassDateModalActive.value = true;
};

const executeMassDateChange = () => {
    if (checkedRows.value.length === 0) return;
    isProcessingMassDate.value = true;

    axios.put(route("file-massaction"), {
        action: "change_dates",
        origin_type: props.originType,
        cr: checkedRows.value,
        start_date: massStartDate.value,
        end_date: massEndDate.value
    }).then(res => {
        isProcessingMassDate.value = false;
        isMassDateModalActive.value = false;
        checkedRows.value = [];
        displayNotification(res.data.message || "Fechas actualizadas con éxito", "success");
        router.reload({ preserveScroll: true });
    }).catch(() => {
        isProcessingMassDate.value = false;
        displayNotification("Error al actualizar fechas masivamente", "danger");
    });
};

// Opciones de destino para acciones masivas
const massOptions = computed(() => {
    const defaultOption = { id: 0, label: 'Compartido con todos', label_object: 'Compartido con todos', is_parent: true, bold: true };
    const list = [defaultOption];
    if (props.objectTarget && Array.isArray(props.objectTarget) && props.objectTarget.length > 0) {
        props.objectTarget.forEach(item => {
            list.push({
                ...item,
                label: item.label || item.label_object
            });
        });
    }
    return list;
});

const executeMassChange = () => {
    if (checkedRows.value.length === 0) return;
    isProcessingMass.value = true;

    axios.put(route("file-massaction"), {
        action: "change_src_object_id",
        origin_type: props.originType,
        cr: checkedRows.value,
        new_src_object_id: massTargetId.value
    }).then(res => {
        isProcessingMass.value = false;
        isMassModalActive.value = false;
        checkedRows.value = [];
        displayNotification(res.data.message || "Acción masiva ejecutada con éxito", "success");
        router.reload({ preserveScroll: true });
    }).catch(() => {
        isProcessingMass.value = false;
        displayNotification("Error al ejecutar la acción masiva", "danger");
    });
};

// Notificaciones flotantes
const showNotification = ref(false);
const notificationColor = ref("success");
const notificationMessage = ref("");
let notificationTimeout = null;

const displayNotification = (msg, color = "success") => {
    notificationMessage.value = msg;
    notificationColor.value = color;
    showNotification.value = true;
    if (notificationTimeout) clearTimeout(notificationTimeout);
    notificationTimeout = setTimeout(() => (showNotification.value = false), 4000);
};

// Modal de Vista Previa (Preview.vue)
const isPreviewActive = ref(false);
const previewFile = ref(null);

const openPreview = (row) => {
    previewFile.value = {
        id: row.id,
        name: row.name || row.filename,
        filename: row.filename,
        filepath: row.filepath,
        type: row.type || (row.filename ? row.filename.split(".").pop() : "")
    };
    isPreviewActive.value = true;
};

// Modal de Eliminación Individual
const isDeleteModalActive = ref(false);
const fileToDelete = ref(null);

const confirmDelete = (row) => {
    fileToDelete.value = row;
    isDeleteModalActive.value = true;
};

const executeDelete = () => {
    if (!fileToDelete.value) return;
    router.post(route(props.ruta + "-eliminar"), {
        id: fileToDelete.value.id
    }, {
        onSuccess: () => {
            isDeleteModalActive.value = false;
            fileToDelete.value = null;
            displayNotification("Archivo eliminado correctamente", "success");
        }
    });
};

// Formateo de celdas sin desfase de zona horaria (evita retroceder 1 día)
const formatCellValue = (col, val) => {
    if (val === undefined || val === null || val === "") return "-";
    if (col.replace && col.replace[val] !== undefined) return col.replace[val];

    if (col.type === "date") {
        if (typeof val === "string") {
            const parts = val.split("-");
            if (parts.length === 3) {
                return `${parseInt(parts[2], 10)}/${parseInt(parts[1], 10)}/${parts[0]}`;
            }
        }
        try {
            const d = new Date(val + "T12:00:00");
            if (!isNaN(d)) return d.toLocaleDateString("es-ES");
        } catch (e) {}
    }

    if (col.type === "datetime") {
        try {
            const d = new Date(val);
            if (!isNaN(d)) {
                return d.toLocaleDateString("es-ES") + " " + d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
            }
        } catch (e) {}
    }

    return val;
};
</script>

<template>
    <div class="space-y-4">
        <!-- Notificación -->
        <NotificationAlert v-model="showNotification" :message="notificationMessage" :color="notificationColor" class="mb-2" />

        <!-- Barra de Herramientas Superior -->
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-gray-50/70 dark:bg-slate-800/40 p-3 rounded-2xl border border-gray-200 dark:border-slate-800">
            <!-- Búsqueda General -->
            <div class="relative flex-1 max-w-md">
                <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                <input
                    v-model="searchGeneral"
                    type="text"
                    placeholder="Buscar en todos los campos..."
                    class="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-gray-700 dark:text-gray-200"
                />
            </div>

            <!-- Acciones y Filtros rápidos -->
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    @click="clearFilters"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-xl hover:bg-gray-50 transition shadow-sm"
                    title="Limpiar todos los filtros"
                >
                    <BaseIcon :path="mdiBroom" :size="16" />
                    <span>Limpiar Filtros</span>
                </button>

                <div class="text-xs text-gray-500 dark:text-gray-400 font-medium pl-2">
                    {{ filteredData.length }} {{ filteredData.length === 1 ? 'registro' : 'registros' }}
                </div>
            </div>
        </div>

        <!-- Barra flotante de Acciones Masivas -->
        <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div
                v-if="checkedRows.length > 0"
                class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800/80 rounded-2xl shadow-sm text-xs"
            >
                <div class="flex items-center gap-2 font-bold text-blue-900 dark:text-blue-200">
                    <i class="fa-solid fa-circle-check text-blue-600"></i>
                    <span>{{ checkedRows.length }} {{ checkedRows.length === 1 ? 'archivo seleccionado' : 'archivos seleccionados' }}</span>
                </div>

                <div class="flex items-center flex-wrap gap-2">
                    <!-- Acción 1: Cambiar Destino -->
                    <button
                        type="button"
                        @click="openMassChangeModal"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-sm transition"
                    >
                        <BaseIcon :path="mdiLayersTriple" :size="16" />
                        <span>Cambiar Nivel / Curso</span>
                    </button>

                    <!-- Acción 2: Actualizar Fechas -->
                    <button
                        type="button"
                        @click="openMassDateModal"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-sm transition"
                    >
                        <BaseIcon :path="mdiCalendarRange" :size="16" />
                        <span>Actualizar Fechas</span>
                    </button>

                    <button
                        type="button"
                        @click="checkedRows = []"
                        class="px-2 py-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 rounded-lg hover:bg-blue-100/50 dark:hover:bg-slate-800 transition"
                    >
                        Deseleccionar
                    </button>
                </div>
            </div>
        </Transition>

        <!-- Tabla Responsiva -->
        <div class="overflow-x-auto rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
            <table class="w-full text-left text-xs text-gray-600 dark:text-gray-300">
                <!-- Header con títulos y ordenamiento -->
                <thead class="bg-gray-50/90 dark:bg-slate-800/90 text-gray-700 dark:text-gray-200 font-bold uppercase text-[10px] tracking-wider border-b border-gray-200 dark:border-slate-700">
                    <tr>
                        <th class="p-3 w-10 text-center">
                            <input
                                type="checkbox"
                                :checked="isAllChecked"
                                @change="toggleSelectAll"
                                class="rounded text-blue-600 focus:ring-blue-500 dark:bg-slate-900 border-gray-300 dark:border-slate-700"
                            />
                        </th>
                        <th
                            v-for="col in cols"
                            :key="col.key"
                            @click="setSort(col.key)"
                            class="p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700/50 transition select-none"
                        >
                            <div class="flex items-center gap-1">
                                <span>{{ col.label }}</span>
                                <span v-if="sortKey === col.key" class="text-blue-600">
                                    {{ sortAsc ? '▲' : '▼' }}
                                </span>
                            </div>
                        </th>
                        <th class="p-3 text-right">Acciones</th>
                    </tr>

                    <!-- Fila de Filtros por Columna -->
                    <tr class="bg-gray-50/40 dark:bg-slate-800/40 border-t border-gray-100 dark:border-slate-800/60 font-normal">
                        <td class="p-2"></td>
                        <td v-for="col in cols" :key="'filter-' + col.key" class="p-2">
                            <!-- Si es select / reemplazo -->
                            <div v-if="col.filterType === 'select'" class="min-w-[130px]">
                                <SelectSearch
                                    v-model="filters[col.key]"
                                    :options="getSelectFilterOptions(col)"
                                    :getOptionValue="(opt) => opt.id"
                                    :getOptionLabel="(opt) => opt.label"
                                    placeholder="Todos"
                                    :clearable="true"
                                />
                            </div>
                            <!-- Input de texto normal -->
                            <input
                                v-else
                                v-model="filters[col.key]"
                                type="text"
                                placeholder="Filtrar..."
                                class="w-full px-2 py-1 text-[11px] bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-1 focus:ring-blue-500 text-gray-700 dark:text-gray-200"
                            />
                        </td>
                        <td class="p-2"></td>
                    </tr>
                </thead>

                <!-- Filas de Datos -->
                <tbody class="divide-y divide-gray-100 dark:divide-slate-800 font-normal">
                    <tr
                        v-for="row in paginatedData"
                        :key="row.id"
                        class="hover:bg-blue-50/30 dark:hover:bg-slate-800/50 transition-colors"
                        :class="{ 'bg-blue-50/50 dark:bg-blue-950/30': checkedRows.includes(row.id) }"
                    >
                        <td class="p-3 text-center">
                            <input
                                type="checkbox"
                                :checked="checkedRows.includes(row.id)"
                                @change="toggleRowCheck(row.id)"
                                class="rounded text-blue-600 focus:ring-blue-500 dark:bg-slate-900 border-gray-300 dark:border-slate-700"
                            />
                        </td>

                        <td v-for="col in cols" :key="'cell-' + row.id + '-' + col.key" class="p-3">
                            <template v-if="col.key === 'name' || col.key === 'filename'">
                                <div class="flex items-center gap-2">
                                    <BaseIcon :path="mdiFileDocumentOutline" :size="18" class="text-blue-500 shrink-0" />
                                    <span class="font-semibold text-gray-800 dark:text-gray-100">{{ formatCellValue(col, row[col.key]) }}</span>
                                </div>
                            </template>
                            <template v-else-if="col.key === 'position'">
                                <span class="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 font-semibold text-[11px]">
                                    #{{ row[col.key] }}
                                </span>
                            </template>
                            <template v-else>
                                {{ formatCellValue(col, row[col.key]) }}
                            </template>
                        </td>

                        <!-- Acciones por fila -->
                        <td class="p-3 text-right">
                            <div class="flex items-center justify-end gap-1.5">
                                <!-- Botón Vista Previa -->
                                <button
                                    v-if="canView"
                                    type="button"
                                    @click="openPreview(row)"
                                    class="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 rounded-lg transition-colors shadow-sm"
                                    title="Vista Previa"
                                >
                                    <BaseIcon :path="mdiEye" :size="16" />
                                </button>

                                <!-- Botón Editar -->
                                <Link
                                    v-if="canEdit"
                                    :href="route('file-edit', row.id)"
                                    class="p-1.5 text-amber-600 hover:text-amber-800 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 rounded-lg transition-colors shadow-sm"
                                    title="Editar"
                                >
                                    <BaseIcon :path="mdiPencil" :size="16" />
                                </Link>

                                <!-- Botón Descargar -->
                                <a
                                    v-if="canDownload && (row.filepath && row.filename)"
                                    :href="'/documents/' + row.filepath + '/' + row.filename"
                                    target="_blank"
                                    download
                                    class="p-1.5 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 rounded-lg transition-colors shadow-sm"
                                    title="Descargar archivo"
                                >
                                    <BaseIcon :path="mdiDownload" :size="16" />
                                </a>

                                <!-- Botón Eliminar -->
                                <button
                                    v-if="deleteable"
                                    type="button"
                                    @click="confirmDelete(row)"
                                    class="p-1.5 text-red-600 hover:text-red-800 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 rounded-lg transition-colors shadow-sm"
                                    title="Eliminar"
                                >
                                    <BaseIcon :path="mdiTrashCan" :size="16" />
                                </button>
                            </div>
                        </td>
                    </tr>

                    <tr v-if="paginatedData.length === 0">
                        <td :colspan="cols.length + 2" class="p-8 text-center text-gray-400 italic">
                            No se encontraron archivos con los criterios especificados.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Paginación -->
        <div class="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500 pt-2">
            <div class="flex items-center gap-2">
                <span>Mostrar</span>
                <select
                    v-model="perPage"
                    class="py-1 px-2 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg"
                >
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
                <span>filas por página</span>
            </div>

            <div class="flex items-center gap-2">
                <span>Página {{ currentPage }} de {{ totalPages }}</span>
                <div class="flex items-center gap-1">
                    <button
                        type="button"
                        :disabled="currentPage === 1"
                        @click="currentPage--"
                        class="p-1.5 border border-gray-300 dark:border-slate-700 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <BaseIcon :path="mdiChevronLeft" :size="16" />
                    </button>
                    <button
                        type="button"
                        :disabled="currentPage === totalPages"
                        @click="currentPage++"
                        class="p-1.5 border border-gray-300 dark:border-slate-700 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <BaseIcon :path="mdiChevronRight" :size="16" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Modal de Vista Previa -->
        <Preview v-model="isPreviewActive" :file="previewFile" :onlyread="false" />

        <!-- Modal de Acciones Masivas: Destino -->
        <CardBoxModal
            v-model="isMassModalActive"
            title="Cambio Masivo de Destino (Nivel / Curso)"
            button="info"
            hasCancel
            @confirm="executeMassChange"
        >
            <div class="space-y-4">
                <p class="text-sm text-gray-600 dark:text-gray-300">
                    Se actualizará el <b>nivel educativo / curso</b> de los <b>{{ checkedRows.length }}</b> archivos seleccionados:
                </p>

                <div>
                    <label class="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                        Selecciona el nuevo nivel / curso:
                    </label>
                    <SelectSearch
                        v-model="massTargetId"
                        :options="massOptions"
                        :getOptionValue="(opt) => opt.id"
                        :getOptionLabel="(opt) => opt.label || opt.label_object"
                        placeholder="Seleccione..."
                    />
                </div>
            </div>
        </CardBoxModal>

        <!-- Modal de Acciones Masivas: Fechas de Inicio y Fin -->
        <CardBoxModal
            v-model="isMassDateModalActive"
            title="Actualización Masiva de Fechas de Vigencia"
            button="info"
            hasCancel
            @confirm="executeMassDateChange"
        >
            <div class="space-y-4">
                <p class="text-sm text-gray-600 dark:text-gray-300">
                    Se actualizarán las fechas de vigencia para los <b>{{ checkedRows.length }}</b> archivos seleccionados:
                </p>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                            Fecha de Inicio:
                        </label>
                        <input
                            v-model="massStartDate"
                            type="date"
                            class="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-gray-700 dark:text-gray-200"
                        />
                    </div>
                    <div>
                        <label class="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                            Fecha de Fin:
                        </label>
                        <input
                            v-model="massEndDate"
                            type="date"
                            class="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-gray-700 dark:text-gray-200"
                        />
                    </div>
                </div>
                <p class="text-[11px] text-gray-400 italic">
                    * Rango sugerido por defecto calculado con la constante de {{ filesDateDiffDays }} días.
                </p>
            </div>
        </CardBoxModal>

        <!-- Modal de Confirmación de Eliminación -->
        <CardBoxModal
            v-model="isDeleteModalActive"
            title="Confirmar eliminación"
            button="danger"
            hasCancel
            @confirm="executeDelete"
        >
            <p>
                ¿Está seguro de que desea eliminar el archivo <b>{{ fileToDelete?.name || fileToDelete?.filename }}</b>?
            </p>
        </CardBoxModal>
    </div>
</template>